import React, { useEffect, useState, useMemo, useCallback } from "react";
import config from '../config.js';
import { useThemeMode } from '../ThemeContext';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from 'recharts';

const API_BASE = config.API_BASE_URL;

const TIME_RANGES = [
  { label: "1Y", days: 365 },
  { label: "3Y", days: 3 * 365 },
  { label: "5Y", days: 5 * 365 },
  { label: "MAX", days: null },
];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function formatPrice(val) {
  if (val == null) return "—";
  return `$${Number(val).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function formatPct(val) {
  if (val == null) return "—";
  const sign = val > 0 ? "+" : "";
  return `${sign}${val.toFixed(1)}%`;
}

export default function SteelMarketPage() {
  const { isDark } = useThemeMode();

  // Data states
  const [priceData, setPriceData] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [cwtComparison, setCwtComparison] = useState([]);
  const [commentary, setCommentary] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [seasonality, setSeasonality] = useState(null);
  const [economic, setEconomic] = useState(null);
  const [imports, setImports] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [newAlertType, setNewAlertType] = useState("price_below");
  const [newAlertThreshold, setNewAlertThreshold] = useState("");
  const [newAlertNote, setNewAlertNote] = useState("");
  const [purchases, setPurchases] = useState(null);
  const [showPurchaseDetail, setShowPurchaseDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI states
  const [timeRange, setTimeRange] = useState("3Y");
  const [lastUpdated, setLastUpdated] = useState(null);

  const startDate = useMemo(() => {
    const range = TIME_RANGES.find(r => r.label === timeRange);
    if (!range || !range.days) return null;
    const d = new Date();
    d.setDate(d.getDate() - range.days);
    return d.toISOString().split("T")[0];
  }, [timeRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = startDate ? `?start_date=${startDate}` : "";
      const [pricesRes, analysisRes, cwtRes, commentaryRes, indicatorsRes, seasonalityRes, economicRes, importsRes, purchasesRes] = await Promise.all([
        fetch(`${API_BASE}/steel-market/prices${params}`),
        fetch(`${API_BASE}/steel-market/analysis`),
        fetch(`${API_BASE}/steel-market/vs-cwt${params}`),
        fetch(`${API_BASE}/steel-market/commentary`),
        fetch(`${API_BASE}/steel-market/indicators${params}`),
        fetch(`${API_BASE}/steel-market/seasonality`),
        fetch(`${API_BASE}/steel-market/economic${params}`),
        fetch(`${API_BASE}/steel-market/imports${params}`),
        fetch(`${API_BASE}/steel-market/purchases${params ? params : "?start_date=" + new Date(Date.now() - 2 * 365 * 86400000).toISOString().split("T")[0]}`),
      ]);

      if (pricesRes.ok) {
        const pd = await pricesRes.json();
        setPriceData(pd.prices || []);
      }
      if (analysisRes.ok) {
        const ad = await analysisRes.json();
        setAnalysis(ad);
        setLastUpdated(ad.current_date);
      }
      if (cwtRes.ok) {
        const cd = await cwtRes.json();
        setCwtComparison(cd.comparison || []);
      }
      if (commentaryRes.ok) {
        const cm = await commentaryRes.json();
        setCommentary(cm);
      }
      if (indicatorsRes.ok) {
        const id = await indicatorsRes.json();
        setIndicators(id.indicators || null);
      }
      if (seasonalityRes.ok) {
        const sd = await seasonalityRes.json();
        setSeasonality(sd);
      }
      if (economicRes.ok) {
        const ed = await economicRes.json();
        setEconomic(ed);
      }
      if (importsRes.ok) {
        const im = await importsRes.json();
        setImports(im);
      }
      if (purchasesRes.ok) {
        const pu = await purchasesRes.json();
        setPurchases(pu);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/steel-market/alerts`);
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) { /* ignore */ }
  }, []);

  const createAlert = async () => {
    if (!newAlertType) return;
    const params = new URLSearchParams({ alert_type: newAlertType, threshold: newAlertThreshold || "0" });
    if (newAlertNote) params.append("note", newAlertNote);
    try {
      await fetch(`${API_BASE}/steel-market/alerts?${params}`, { method: "POST" });
      setNewAlertThreshold("");
      setNewAlertNote("");
      fetchAlerts();
    } catch (err) { /* ignore */ }
  };

  const deleteAlert = async (id) => {
    try {
      await fetch(`${API_BASE}/steel-market/alerts/${id}`, { method: "DELETE" });
      fetchAlerts();
    } catch (err) { /* ignore */ }
  };

  const toggleAlert = async (id, enabled) => {
    try {
      await fetch(`${API_BASE}/steel-market/alerts/${id}/toggle?enabled=${!enabled}`, { method: "PUT" });
      fetchAlerts();
    } catch (err) { /* ignore */ }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Chart data with moving averages
  const chartData = useMemo(() => {
    if (!priceData.length || !analysis) return [];
    const ma6m = analysis.moving_avg_6m;
    const ma12m = analysis.moving_avg_12m;
    return priceData.map(p => ({
      date: p.date,
      label: formatDate(p.date),
      close: p.close,
      high: p.high,
      low: p.low,
      ma6m,
      ma12m,
    }));
  }, [priceData, analysis]);

  // Month-over-month data for bar chart
  const momData = useMemo(() => {
    if (!analysis?.monthly_changes) return [];
    return analysis.monthly_changes.map(m => ({
      month: m.month,
      label: formatDate(m.month + "-01"),
      change: m.change_pct,
      fill: m.change_pct <= 0 ? "#22c55e" : "#ef4444",
    }));
  }, [analysis]);

  // CWT comparison chart data
  const cwtData = useMemo(() => {
    return cwtComparison.filter(c => c.market_price || c.avg_cwt_paid).map(c => ({
      month: c.month,
      label: formatDate(c.month + "-01"),
      market: c.market_price || null,
      cwt: c.avg_cwt_paid || null,
    }));
  }, [cwtComparison]);

  // Supplier spread data — only months where both lines exist
  const spreadData = useMemo(() => {
    const withBoth = cwtData.filter(c => c.market != null && c.cwt != null);
    if (withBoth.length === 0) return { data: [], avg: 0 };
    const data = withBoth.map(c => {
      const spread = +(c.cwt - c.market).toFixed(2);
      const pct = +((spread / c.market) * 100).toFixed(1);
      return { ...c, spread, pct };
    });
    const avg = +(data.reduce((s, d) => s + d.spread, 0) / data.length).toFixed(2);
    return { data, avg };
  }, [cwtData]);

  // Leading indicators chart data — monthly HRC + scrap + iron ore normalized to index (100 = first month)
  const indicatorChartData = useMemo(() => {
    if (!indicators || !priceData.length) return [];
    // Build monthly HRC
    const hrcMonthly = {};
    for (const p of priceData) {
      const m = p.date.slice(0, 7);
      hrcMonthly[m] = p.close;
    }
    const scrapMonthly = {};
    const ironMonthly = {};
    if (indicators.scrap?.monthly) {
      for (const m of indicators.scrap.monthly) scrapMonthly[m.month] = m.close;
    }
    if (indicators.iron_ore?.monthly) {
      for (const m of indicators.iron_ore.monthly) ironMonthly[m.month] = m.close;
    }
    const allMonths = [...new Set([
      ...Object.keys(hrcMonthly),
      ...Object.keys(scrapMonthly),
      ...Object.keys(ironMonthly),
    ])].sort();
    if (!allMonths.length) return [];
    // Normalize: first value = 100
    const hrcBase = hrcMonthly[allMonths.find(m => hrcMonthly[m])] || 1;
    const scrapBase = scrapMonthly[allMonths.find(m => scrapMonthly[m])] || 1;
    const ironBase = ironMonthly[allMonths.find(m => ironMonthly[m])] || 1;
    return allMonths.map(m => ({
      month: m,
      label: formatDate(m + "-01"),
      hrc: hrcMonthly[m] ? Math.round((hrcMonthly[m] / hrcBase) * 100) : null,
      scrap: scrapMonthly[m] ? Math.round((scrapMonthly[m] / scrapBase) * 100) : null,
      iron_ore: ironMonthly[m] ? Math.round((ironMonthly[m] / ironBase) * 100) : null,
      hrc_price: hrcMonthly[m] || null,
      scrap_price: scrapMonthly[m] || null,
      iron_price: ironMonthly[m] || null,
    }));
  }, [indicators, priceData]);

  // Seasonality chart data
  const seasonalChartData = useMemo(() => {
    if (!seasonality?.pattern) return [];
    return seasonality.pattern.map(s => ({
      ...s,
      currentYear: seasonality.current_year?.find(c => c.month === s.month)?.index || null,
    }));
  }, [seasonality]);

  const chartColors = {
    primary: isDark ? "#60a5fa" : "#3b82f6",
    secondary: isDark ? "#34d399" : "#10b981",
    grid: isDark ? "#374151" : "#e5e7eb",
    text: isDark ? "#9ca3af" : "#6b7280",
    bg: isDark ? "#1f2937" : "#ffffff",
    cardBg: isDark ? "#111827" : "#f9fafb",
  };

  const tooltipStyle = {
    contentStyle: { background: chartColors.bg, border: `1px solid ${chartColors.grid}`, borderRadius: 8, fontSize: 13 },
    labelStyle: { color: isDark ? "#e5e7eb" : "#111827" },
    itemStyle: { color: isDark ? "#d1d5db" : "#374151" },
  };

  if (loading && !analysis) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 text-muted">Loading steel market data...</p>
        </div>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">Failed to load market data: {error}</div>
      </div>
    );
  }

  const recColor = analysis?.recommendation === "BUY"
    ? "success" : analysis?.recommendation === "WAIT" ? "warning" : "secondary";

  const changeColor = (val) => {
    if (val == null) return "";
    // For steel buying: price going DOWN is good (green), UP is bad (red)
    return val <= 0 ? "text-success" : "text-danger";
  };

  return (
    <div className="container-fluid py-3 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-bold">Steel Market Intelligence</h4>
          <small className="text-muted">
            CME US Midwest HRC Futures (HRC=F)
            {lastUpdated && ` — Last updated: ${lastUpdated}`}
          </small>
        </div>
        <button className="btn btn-sm btn-outline-primary" onClick={fetchData} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-3">
        <div className="col-lg-3 col-sm-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <small className="text-muted text-uppercase fw-semibold">Current Price</small>
              <h2 className="mb-0 fw-bold">{formatPrice(analysis?.current_price)}</h2>
              <small className="text-muted">per short ton</small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-sm-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <small className="text-muted text-uppercase fw-semibold">30-Day Change</small>
              <h2 className={`mb-0 fw-bold ${changeColor(analysis?.change_30d_pct)}`}>
                {formatPct(analysis?.change_30d_pct)}
                {analysis?.change_30d_pct > 0 ? " ▲" : analysis?.change_30d_pct < 0 ? " ▼" : ""}
              </h2>
              <small className="text-muted text-capitalize">{analysis?.trend_direction} ({analysis?.trend_strength})</small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-sm-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <small className="text-muted text-uppercase fw-semibold">1-Year Change</small>
              <h2 className={`mb-0 fw-bold ${changeColor(analysis?.change_1y_pct)}`}>
                {formatPct(analysis?.change_1y_pct)}
              </h2>
              <small className="text-muted">
                from {formatPrice(analysis?.price_1y_ago)}
              </small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-sm-6">
          <div className={`card h-100 shadow-sm border-0 bg-${recColor} bg-opacity-10`}>
            <div className="card-body text-center">
              <small className="text-muted text-uppercase fw-semibold">Recommendation</small>
              <h2 className={`mb-0 fw-bold text-${recColor}`}>
                {analysis?.recommendation || "—"}
              </h2>
              <small className="text-muted">
                {analysis?.signals?.filter(s => s.type === "bullish").length || 0} bullish /
                {" "}{analysis?.signals?.filter(s => s.type === "bearish").length || 0} bearish
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* AI Commentary */}
      {commentary?.commentary && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body py-3">
            <div className="d-flex align-items-start">
              <span className="badge bg-primary bg-opacity-10 text-primary me-2 mt-1"
                    style={{ fontSize: "0.7rem" }}>AI</span>
              <p className="mb-0" style={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
                {commentary.commentary}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tariff & Trade Policy Reference */}
      <div className="card shadow-sm border-0 mb-3" style={{ borderLeft: `3px solid #f59e0b` }}>
        <div className="card-body py-2 d-flex align-items-center gap-3">
          <span className="badge bg-warning text-dark" style={{ fontSize: "0.7rem" }}>TARIFF</span>
          <div style={{ fontSize: "0.85rem" }}>
            <strong>Section 232:</strong> 25% tariff on all steel imports (no country exemptions).
            Applies to HRC, CRC, plate, structural, pipe, and derivative products.
          </div>
        </div>
      </div>

      {/* Price History Chart */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0 fw-bold">Price History</h6>
            <small className="text-muted">Daily settlement prices with moving averages</small>
          </div>
          <div className="btn-group btn-group-sm">
            {TIME_RANGES.map(r => (
              <button
                key={r.label}
                className={`btn ${timeRange === r.label ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setTimeRange(r.label)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: chartColors.text }}
                interval="preserveStartEnd"
                minTickGap={60}
              />
              <YAxis
                tick={{ fontSize: 11, fill: chartColors.text }}
                domain={["auto", "auto"]}
                tickFormatter={v => `$${v}`}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(val, name) => {
                  const label = name === "close" ? "Price" : name === "ma6m" ? "6M Avg" : "12M Avg";
                  return [formatPrice(val), label];
                }}
                labelFormatter={(label) => label}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => {
                  if (value === "close") return "HRC Price";
                  if (value === "ma6m") return "6-Month MA";
                  if (value === "ma12m") return "12-Month MA";
                  return value;
                }}
              />
              <Area
                type="monotone"
                dataKey="close"
                stroke={chartColors.primary}
                fill="url(#priceGradient)"
                strokeWidth={2}
                dot={false}
              />
              {analysis && (
                <ReferenceLine
                  y={analysis.moving_avg_6m}
                  stroke="#f59e0b"
                  strokeDasharray="6 3"
                  strokeWidth={1.5}
                  label={false}
                />
              )}
              {analysis && (
                <ReferenceLine
                  y={analysis.moving_avg_12m}
                  stroke="#8b5cf6"
                  strokeDasharray="6 3"
                  strokeWidth={1.5}
                  label={false}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Leading Indicators */}
      {indicatorChartData.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-transparent border-0">
            <div>
              <h6 className="mb-0 fw-bold">Leading Indicators</h6>
              <small className="text-muted">
                Scrap &amp; iron ore prices move before HRC — normalized index (100 = start of range)
              </small>
            </div>
          </div>
          <div className="card-body">
            {/* Indicator KPI pills */}
            {indicators && (
              <div className="d-flex flex-wrap gap-3 mb-3">
                {Object.entries(indicators).map(([key, ind]) => {
                  if (!ind.analysis || ind.analysis.error) return null;
                  const a = ind.analysis;
                  const dirColor = a.direction === "rising" ? "text-danger" : a.direction === "falling" ? "text-success" : "text-muted";
                  return (
                    <div key={key} className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                         style={{ background: isDark ? "#1e293b" : "#f1f5f9" }}>
                      <div>
                        <small className="text-muted d-block">{ind.name}</small>
                        <span className="fw-bold">{formatPrice(a.current_price)}</span>
                        <small className={dirColor}> {formatPct(a.change_30d_pct)} (30d)</small>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={indicatorChartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: chartColors.text }}
                         interval="preserveStartEnd" minTickGap={60} />
                  <YAxis tick={{ fontSize: 11, fill: chartColors.text }}
                         domain={["auto", "auto"]} />
                  <Tooltip
                    {...tooltipStyle}
                    formatter={(val, name, props) => {
                      const p = props.payload;
                      if (name === "hrc") return [p.hrc_price ? `$${p.hrc_price.toLocaleString()}/ton` : "—", "HRC"];
                      if (name === "scrap") return [p.scrap_price ? `$${p.scrap_price.toLocaleString()}/ton` : "—", "Busheling Scrap"];
                      if (name === "iron_ore") return [p.iron_price ? `$${p.iron_price.toLocaleString()}/ton` : "—", "Iron Ore 62% Fe"];
                      return [val, name];
                    }}
                  />
                  <Legend formatter={v => v === "hrc" ? "HRC" : v === "scrap" ? "Busheling Scrap" : "Iron Ore 62% Fe"} />
                  <Line type="monotone" dataKey="hrc" stroke={chartColors.primary}
                        strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="scrap" stroke="#f59e0b"
                        strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="iron_ore" stroke="#ef4444"
                        strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Supply & Demand Indicators (FRED) */}
      {economic?.lead_time_estimate && (
        <div className="row g-3 mb-3">
          {/* Lead Time Estimate */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0">
                <h6 className="mb-0 fw-bold">Est. Mill Lead Time</h6>
                <small className="text-muted">Based on capacity utilization</small>
              </div>
              <div className="card-body text-center">
                {(() => {
                  const lt = economic.lead_time_estimate;
                  const tColors = {
                    very_tight: { bg: "#fecaca", text: "#dc2626", label: "Very Tight" },
                    tight: { bg: "#fed7aa", text: "#ea580c", label: "Tight" },
                    normal: { bg: "#d1fae5", text: "#059669", label: "Normal" },
                    loose: { bg: "#dbeafe", text: "#2563eb", label: "Loose" },
                    very_loose: { bg: "#e0e7ff", text: "#4f46e5", label: "Very Loose" },
                  };
                  const tc = tColors[lt.supply_tightness] || tColors.normal;
                  return (
                    <>
                      <div className="rounded-3 py-3 px-4 mb-2" style={{ background: isDark ? "#1e293b" : tc.bg }}>
                        <h3 className="fw-bold mb-0" style={{ color: tc.text }}>{lt.estimated_weeks}</h3>
                        <span className="badge mt-1" style={{ background: tc.text, color: "#fff" }}>{tc.label}</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted d-block">Capacity Utilization</small>
                        <span className="fw-bold fs-5">{lt.capacity_utilization?.toFixed(1)}%</span>
                        {economic.capacity_trend && (
                          <small className={`ms-2 ${economic.capacity_trend === "rising" ? "text-danger" : economic.capacity_trend === "falling" ? "text-success" : "text-muted"}`}>
                            {economic.capacity_trend === "rising" ? "trending up" : economic.capacity_trend === "falling" ? "trending down" : "stable"}
                          </small>
                        )}
                      </div>
                      <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8rem" }}>{lt.note}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Capacity Utilization Chart */}
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0">
                <h6 className="mb-0 fw-bold">Capacity Utilization &amp; Production</h6>
                <small className="text-muted">FRED: Primary metals capacity utilization (%) and production index</small>
              </div>
              <div className="card-body" style={{ height: 320 }}>
                {(() => {
                  const capData = economic.series?.capacity_util?.data || [];
                  const prodData = economic.series?.production_idx?.data || [];
                  // Merge by date
                  const merged = {};
                  for (const d of capData) {
                    merged[d.date] = { date: d.date, label: formatDate(d.date.slice(0, 7) + "-01"), cap: d.value };
                  }
                  for (const d of prodData) {
                    if (!merged[d.date]) merged[d.date] = { date: d.date, label: formatDate(d.date.slice(0, 7) + "-01") };
                    merged[d.date].prod = d.value;
                  }
                  const chartD = Object.values(merged).sort((a, b) => a.date.localeCompare(b.date));
                  if (!chartD.length) return <div className="text-center text-muted py-5">No FRED data available. Set FRED_API_KEY on the Lambda.</div>;
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartD} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: chartColors.text }}
                               interval="preserveStartEnd" minTickGap={60} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: chartColors.text }}
                               domain={[60, 90]} tickFormatter={v => `${v}%`} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: chartColors.text }} />
                        <Tooltip
                          {...tooltipStyle}
                          formatter={(val, name) => [
                            name === "cap" ? `${val.toFixed(1)}%` : val.toFixed(1),
                            name === "cap" ? "Capacity Util %" : "Production Index"
                          ]}
                        />
                        <Legend formatter={v => v === "cap" ? "Capacity Utilization %" : "Production Index"} />
                        <ReferenceLine yAxisId="left" y={80} stroke="#ef4444" strokeDasharray="6 3"
                          label={{ value: "Tight Supply", position: "left", fontSize: 10, fill: "#ef4444" }} />
                        <Area yAxisId="left" type="monotone" dataKey="cap" stroke="#f59e0b"
                              fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="prod" stroke={chartColors.primary}
                              strokeWidth={1.5} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Activity */}
      {imports?.data?.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-start">
            <div>
              <h6 className="mb-0 fw-bold">Steel Import Activity (Flat Products)</h6>
              <small className="text-muted">
                US monthly imports — source: Commerce Dept SIMA
              </small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block">Trend</small>
              <span className={`fw-bold ${imports.import_trend === "rising" ? "text-success" : imports.import_trend === "falling" ? "text-danger" : "text-muted"}`}>
                {imports.import_trend === "rising" ? "Rising (bearish for domestic)" : imports.import_trend === "falling" ? "Falling (bullish for domestic)" : "Stable"}
              </span>
            </div>
          </div>
          <div className="card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={imports.data.map(d => ({
                ...d,
                label: formatDate(d.month + "-01"),
                tons_k: Math.round(d.metric_tons / 1000),
              }))} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: chartColors.text }}
                       interval="preserveStartEnd" minTickGap={60} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: chartColors.text }}
                       tickFormatter={v => `${v}K`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: chartColors.text }}
                       tickFormatter={v => `${v}%`} />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(val, name) => {
                    if (name === "tons_k") return [`${val.toLocaleString()}K metric tons`, "Volume"];
                    if (name === "yoy_change_pct") return [val != null ? `${val > 0 ? "+" : ""}${val}%` : "N/A", "YoY Change"];
                    return [val, name];
                  }}
                />
                <Legend formatter={v => v === "tons_k" ? "Import Volume (K MT)" : "YoY Change %"} />
                <Bar yAxisId="left" dataKey="tons_k" radius={[4, 4, 0, 0]} maxBarSize={30}
                     fill={chartColors.primary} fillOpacity={0.7} />
                <Line yAxisId="right" type="monotone" dataKey="yoy_change_pct" stroke="#f59e0b"
                      strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <ReferenceLine yAxisId="right" y={0} stroke={chartColors.text} strokeDasharray="6 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Middle Row: Signals + Market vs CWT */}
      <div className="row g-3 mb-3">
        {/* Signal Indicators */}
        <div className="col-lg-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-transparent border-0">
              <h6 className="mb-0 fw-bold">Signal Indicators</h6>
              <small className="text-muted">Market condition analysis</small>
            </div>
            <div className="card-body pt-0">
              {analysis?.signals?.map((signal, i) => (
                <div key={i} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <span className={`badge rounded-pill me-2 ${
                      signal.type === "bullish" ? "bg-success" :
                      signal.type === "bearish" ? "bg-danger" : "bg-secondary"
                    }`} style={{ fontSize: "0.65rem", minWidth: 60, textAlign: "center" }}>
                      {signal.type === "bullish" ? "BUY" : signal.type === "bearish" ? "WAIT" : "NEUTRAL"}
                    </span>
                    <span style={{ fontSize: "0.85rem" }}>{signal.label}</span>
                  </div>
                  <small className="text-muted">{signal.detail}</small>
                </div>
              ))}
              {analysis?.recommendation_reason && (
                <div className="mt-3 p-2 rounded" style={{ background: isDark ? "#1e293b" : "#f1f5f9", fontSize: "0.85rem" }}>
                  <strong>Summary:</strong> {analysis.recommendation_reason}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Market vs Your Costs */}
        <div className="col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-transparent border-0">
              <h6 className="mb-0 fw-bold">Market Price vs Your Costs ($/CWT)</h6>
              <small className="text-muted">HRC market vs your carbon-steel sheet/plate (A36 + CS-B) avg purchase cost, in $/CWT (per 100 lbs)</small>
            </div>
            <div className="card-body" style={{ height: 320 }}>
              {cwtData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cwtData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: chartColors.text }}
                      interval="preserveStartEnd"
                      minTickGap={60}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: chartColors.text }}
                      tickFormatter={v => `$${v}`}
                    />
                    <Tooltip
                      {...tooltipStyle}
                      formatter={(val, name) => [
                        formatPrice(val),
                        name === "market" ? "Market HRC ($/CWT)" : "Your Avg A36 Sheet Cost ($/CWT)"
                      ]}
                    />
                    <Legend formatter={v => v === "market" ? "Market HRC ($/CWT)" : "Your Avg A36 Sheet Cost ($/CWT)"} />
                    <Line type="monotone" dataKey="market" stroke={chartColors.primary}
                          strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="cwt" stroke={chartColors.secondary}
                          strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-muted py-5">
                  <p>No purchase order data available for comparison.</p>
                  <small>CWT pricing from purchase orders will appear here once POs are created.</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Spread Analysis */}
      {spreadData.data.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-start">
            <div>
              <h6 className="mb-0 fw-bold">Supplier Spread ($/CWT Over Market)</h6>
              <small className="text-muted">
                How much more you pay vs HRC market — bars turning red flag unusual markup
              </small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block">Avg Spread</small>
              <span className="fw-bold" style={{ fontSize: 18, color: chartColors.secondary }}>
                {formatPrice(spreadData.avg)}/CWT
              </span>
            </div>
          </div>
          <div className="card-body" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={spreadData.data} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: chartColors.text }}
                  interval={0}
                  minTickGap={40}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: chartColors.text }}
                  tickFormatter={v => `$${v}`}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(val, name) => {
                    if (name === "spread") return [formatPrice(val) + "/CWT", "Spread"];
                    if (name === "pct") return [val + "%", "Markup %"];
                    return [val, name];
                  }}
                />
                <ReferenceLine y={spreadData.avg} stroke={chartColors.text} strokeDasharray="6 3"
                  label={{ value: `Avg $${spreadData.avg}`, position: "right", fontSize: 10, fill: chartColors.text }} />
                <Bar dataKey="spread" radius={[4, 4, 0, 0]} maxBarSize={36}>
                  {spreadData.data.map((entry, i) => {
                    const overAvg = entry.spread > spreadData.avg * 1.25;
                    return <Cell key={i} fill={overAvg ? "#ef4444" : chartColors.secondary} />;
                  })}
                </Bar>
                <Line type="monotone" dataKey="pct" stroke={isDark ? "#fbbf24" : "#d97706"}
                      strokeWidth={2} dot={{ r: 3 }} yAxisId="right" name="pct" />
                <YAxis yAxisId="right" orientation="right"
                  tick={{ fontSize: 11, fill: chartColors.text }}
                  tickFormatter={v => `${v}%`}
                />
                <Legend formatter={v => v === "spread" ? "Spread ($/CWT)" : "Markup %"} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Purchase Analysis (vendor breakdown) */}
      {purchases?.vendors?.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-0 fw-bold">Purchase Analysis by Vendor</h6>
              <small className="text-muted">
                A36 steel CWT cost by vendor — {purchases.vendor_count} vendor{purchases.vendor_count !== 1 ? "s" : ""}, avg {formatPrice(purchases.overall_avg_cwt)}/CWT
              </small>
            </div>
            <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowPurchaseDetail(!showPurchaseDetail)}>
              {showPurchaseDetail ? "Hide Details" : "Show Details"}
            </button>
          </div>
          <div className="card-body">
            {/* Vendor CWT chart */}
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                {(() => {
                  // Build per-vendor lines over time
                  const vendorColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
                  const topVendors = purchases.vendors.slice(0, 6);
                  const allMonths = [...new Set(topVendors.flatMap(v => v.months.map(m => m.month)))].sort();
                  const chartD = allMonths.map(month => {
                    const row = { month, label: formatDate(month + "-01") };
                    topVendors.forEach((v, idx) => {
                      const m = v.months.find(x => x.month === month);
                      row[`v${idx}`] = m ? m.avg_cwt : null;
                    });
                    return row;
                  });
                  return (
                    <LineChart data={chartD} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: chartColors.text }}
                             interval="preserveStartEnd" minTickGap={60} />
                      <YAxis tick={{ fontSize: 11, fill: chartColors.text }} tickFormatter={v => `$${v}`} />
                      <Tooltip {...tooltipStyle}
                               formatter={(val, name) => {
                                 const idx = parseInt(name.replace("v", ""));
                                 return [formatPrice(val) + "/CWT", topVendors[idx]?.vendor || name];
                               }} />
                      <Legend formatter={name => {
                        const idx = parseInt(name.replace("v", ""));
                        return topVendors[idx]?.vendor || name;
                      }} />
                      {topVendors.map((v, idx) => (
                        <Line key={idx} type="monotone" dataKey={`v${idx}`}
                              stroke={vendorColors[idx % vendorColors.length]}
                              strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      ))}
                    </LineChart>
                  );
                })()}
              </ResponsiveContainer>
            </div>

            {/* Detailed table (collapsible) */}
            {showPurchaseDetail && purchases.recent_purchases?.length > 0 && (
              <div className="mt-3">
                <h6 className="fw-bold" style={{ fontSize: "0.85rem" }}>Recent PO Line Items (Last 50)</h6>
                <div className="table-responsive" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table className="table table-sm table-striped mb-0" style={{ fontSize: "0.8rem" }}>
                    <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                      <tr>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th>Material</th>
                        <th>Weight (lbs)</th>
                        <th>Cost</th>
                        <th>$/CWT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.recent_purchases.map((p, i) => (
                        <tr key={i}>
                          <td>{p.date}</td>
                          <td>{p.vendor}</td>
                          <td className="text-truncate" style={{ maxWidth: 200 }}>{p.material}</td>
                          <td>{p.weight_lbs?.toLocaleString()}</td>
                          <td>{formatPrice(p.cost)}</td>
                          <td className="fw-bold">{formatPrice(p.cwt_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seasonal Patterns */}
      {seasonalChartData.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-transparent border-0">
            <div>
              <h6 className="mb-0 fw-bold">Seasonal Patterns</h6>
              <small className="text-muted">
                Historical avg price index by month (100 = annual mean) — {seasonality?.years_analyzed || 0} years analyzed
              </small>
            </div>
          </div>
          <div className="card-body">
            {seasonality?.insight && (
              <div className="mb-3 p-2 rounded" style={{ background: isDark ? "#1e293b" : "#f1f5f9", fontSize: "0.85rem" }}>
                {seasonality.insight}
              </div>
            )}
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={seasonalChartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
                  <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: chartColors.text }} />
                  <YAxis tick={{ fontSize: 11, fill: chartColors.text }}
                         domain={[95, 105]} />
                  <Tooltip
                    {...tooltipStyle}
                    formatter={(val, name) => {
                      if (name === "index") return [val, "Historical Avg"];
                      if (name === "currentYear") return [val, `${new Date().getFullYear()}`];
                      return [val, name];
                    }}
                  />
                  <Legend formatter={v => v === "index" ? "Historical Avg" : `${new Date().getFullYear()}`} />
                  <ReferenceLine y={100} stroke={chartColors.text} strokeDasharray="6 3"
                    label={{ value: "Annual Mean", position: "right", fontSize: 10, fill: chartColors.text }} />
                  <Bar dataKey="index" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {seasonalChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.index <= 100 ? "#22c55e" : "#ef4444"} opacity={0.7} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="currentYear" stroke={chartColors.primary}
                        strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Month-over-Month Changes */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-transparent border-0">
          <h6 className="mb-0 fw-bold">Month-over-Month Price Changes</h6>
          <small className="text-muted">
            Green = price dropped (good for buying) / Red = price rose
          </small>
        </div>
        <div className="card-body" style={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={momData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: chartColors.text }}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <YAxis
                tick={{ fontSize: 11, fill: chartColors.text }}
                tickFormatter={v => `${v}%`}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(val) => [`${val > 0 ? "+" : ""}${val.toFixed(1)}%`, "Change"]}
              />
              <ReferenceLine y={0} stroke={chartColors.text} />
              <Bar dataKey="change" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {momData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key Stats Footer */}
      {analysis && (
        <div className="row g-3 mb-3">
          <div className="col-md-3 col-6">
            <div className="card shadow-sm border-0">
              <div className="card-body py-2 text-center">
                <small className="text-muted">6-Month Avg</small>
                <div className="fw-bold">{formatPrice(analysis.moving_avg_6m)}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card shadow-sm border-0">
              <div className="card-body py-2 text-center">
                <small className="text-muted">12-Month Avg</small>
                <div className="fw-bold">{formatPrice(analysis.moving_avg_12m)}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card shadow-sm border-0">
              <div className="card-body py-2 text-center">
                <small className="text-muted">All-Time High</small>
                <div className="fw-bold">{formatPrice(analysis.all_time_high?.value)}</div>
                <small className="text-muted">{analysis.all_time_high?.date}</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card shadow-sm border-0">
              <div className="card-body py-2 text-center">
                <small className="text-muted">All-Time Low</small>
                <div className="fw-bold">{formatPrice(analysis.all_time_low?.value)}</div>
                <small className="text-muted">{analysis.all_time_low?.date}</small>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Price Alerts */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-transparent border-0">
          <h6 className="mb-0 fw-bold">Price Alerts</h6>
          <small className="text-muted">Get Slack notifications when market conditions change</small>
        </div>
        <div className="card-body">
          {/* Create alert form */}
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
            <div>
              <label className="form-label mb-1" style={{ fontSize: "0.75rem" }}>Type</label>
              <select className="form-select form-select-sm" value={newAlertType}
                      onChange={e => setNewAlertType(e.target.value)} style={{ width: 200 }}>
                <option value="price_below">Price Below</option>
                <option value="price_above">Price Above</option>
                <option value="change_30d_exceeds">30-Day Change Exceeds</option>
                <option value="recommendation_buy">Recommendation = BUY</option>
              </select>
            </div>
            {newAlertType !== "recommendation_buy" && (
              <div>
                <label className="form-label mb-1" style={{ fontSize: "0.75rem" }}>
                  {newAlertType.includes("change") ? "% Threshold" : "$/ton"}
                </label>
                <input type="number" className="form-control form-control-sm" value={newAlertThreshold}
                       onChange={e => setNewAlertThreshold(e.target.value)} style={{ width: 120 }}
                       placeholder={newAlertType.includes("change") ? "e.g. 5" : "e.g. 800"} />
              </div>
            )}
            <div>
              <label className="form-label mb-1" style={{ fontSize: "0.75rem" }}>Note (optional)</label>
              <input type="text" className="form-control form-control-sm" value={newAlertNote}
                     onChange={e => setNewAlertNote(e.target.value)} style={{ width: 200 }}
                     placeholder="e.g. Reorder trigger" />
            </div>
            <button className="btn btn-sm btn-primary" onClick={createAlert}>
              Add Alert
            </button>
          </div>

          {/* Alert list */}
          {alerts.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm mb-0" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Threshold</th>
                    <th>Note</th>
                    <th>Last Triggered</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(a => (
                    <tr key={a.id} style={{ opacity: a.enabled ? 1 : 0.5 }}>
                      <td>{a.type_label}</td>
                      <td>{a.alert_type === "recommendation_buy" ? "—" :
                           a.alert_type.includes("change") ? `${a.threshold}%` : formatPrice(a.threshold)}</td>
                      <td className="text-muted">{a.note || "—"}</td>
                      <td className="text-muted">{a.last_triggered_at ? new Date(a.last_triggered_at).toLocaleDateString() : "Never"}</td>
                      <td>
                        <button className={`btn btn-sm ${a.enabled ? "btn-outline-success" : "btn-outline-secondary"}`}
                                onClick={() => toggleAlert(a.id, a.enabled)} style={{ fontSize: "0.7rem", padding: "1px 8px" }}>
                          {a.enabled ? "ON" : "OFF"}
                        </button>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteAlert(a.id)}
                                style={{ fontSize: "0.7rem", padding: "1px 8px" }}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
              No alerts configured. Add one above to get notified via Slack.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
